import React, { useState, useEffect, useCallback } from 'react';
import { callApi } from './api';

function CreateAdminForm({ onAdminCreated, onCancel, owners }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await callApi('createAdminV2', { email, password, displayName, ownerId });
      if (onAdminCreated) onAdminCreated();
    } catch (err) {
      setError(`Kunne ikke opprette admin: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ padding: '20px', border: '1px solid #ccc', borderRadius: '5px', margin: '20px auto', display: 'flex', flexDirection: 'column', gap: '15px', maxWidth: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
      <h4>Opprett Ny Admin (Nivå 2)</h4>
      <div>
        <label htmlFor="create-admin-email" style={{ display: 'block', marginBottom: '5px' }}>E-post</label>
        <input id="create-admin-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="off" />
      </div>
      <div>
        <label htmlFor="create-admin-password" style={{ display: 'block', marginBottom: '5px' }}>Passord</label>
        <input id="create-admin-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="new-password" />
      </div>
      <div>
        <label htmlFor="create-admin-displayName" style={{ display: 'block', marginBottom: '5px' }}>Fullt navn</label>
        <input id="create-admin-displayName" value={displayName} onChange={e => setDisplayName(e.target.value)} required />
      </div>
      <select value={ownerId} onChange={e => setOwnerId(e.target.value)} required>
        <option value="">Velg Eier</option>
        {owners.map(owner => (
          <option key={owner.id} value={owner.id}>{owner.name}</option>
        ))}
      </select>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <button type="button" onClick={onCancel}>Avbryt</button>
        <button type="submit" disabled={loading}>{loading ? 'Oppretter...' : 'Opprett'}</button>
      </div>
      {error && <p style={{ color: 'red' }}>{error}</p>}
    </form>
  );
}

function EditAdminForm({ initialData, onFinished, owners }) {
    const [displayName, setDisplayName] = useState(initialData.displayName || '');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const ownerName = owners.find(o => o.id === initialData.customClaims?.ownerId)?.name || 'Ingen eier valgt';

    const handleUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const payload = { 
                uid: initialData.uid, 
                displayName
            };
            if (password) {
                payload.password = password;
            }
            await callApi('updateUserV2', payload);
            if (onFinished) onFinished();
        } catch (err) {
            setError('Kunne ikke oppdatere admin: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (window.confirm(`Er du sikker på at du vil slette admin "${initialData.displayName}"?`)) {
            setLoading(true);
            setError(null);
            try {
                await callApi('deleteUserV2', { uid: initialData.uid });
                if (onFinished) onFinished();
            } catch (err) {
                setError(`Kunne ikke slette admin: ${err.message}`);
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <form onSubmit={handleUpdate} style={{ background: 'var(--background-light)', color: 'var(--text-light)', padding: '20px', borderRadius: '5px', display: 'flex', flexDirection: 'column', gap: '15px', minWidth: '350px', maxWidth: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
                <h4>Rediger Admin</h4>
                <p>E-post: {initialData.email}</p>
                <p>Eier: <strong>{ownerName}</strong></p>
                <div>
                  <label htmlFor="edit-admin-displayName" style={{ display: 'block', marginBottom: '5px' }}>Fullt navn</label>
                  <input id="edit-admin-displayName" value={displayName} onChange={e => setDisplayName(e.target.value)} required />
                </div>
                <div>
                  <label htmlFor="edit-admin-password" style={{ display: 'block', marginBottom: '5px' }}>Nytt passord (valgfritt)</label>
                  <input id="edit-admin-password" type="password" value={password} onChange={e => setPassword(e.target.value)} />
                </div>
                {error && <p style={{ color: 'red' }}>{error}</p>}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                    <button type="button" onClick={handleDelete} disabled={loading} style={{backgroundColor: '#ff4d4d'}}>Slett</button>
                    <div>
                        <button type="button" onClick={onFinished} style={{marginRight: '10px'}}>Avbryt</button>
                        <button type="submit" disabled={loading}>{loading ? 'Lagrer...' : 'Lagre'}</button>
                    </div>
                </div>
            </form>
        </div>
    );
}

function AdminManagement({ onViewUsers }) {
  const [admins, setAdmins] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchData = useCallback(async () => {
    setError(null);
    try {
      const [adminsResult, ownersResult] = await Promise.all([
        callApi('listAdminsV2'),
        callApi('listOwnersV2')
      ]);
      setAdmins(adminsResult.users);
      setOwners(ownersResult.owners);
    } catch (err) {
      setError('Kunne ikke hente data. ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  const handleFormFinished = () => {
    setShowCreateForm(false);
    setEditingAdmin(null);
    fetchData();
  };

  const getOwnerName = (ownerId) => {
    const owner = owners.find(o => o.id === ownerId);
    return owner ? owner.name : <em style={{color: 'gray'}}>Ikke tildelt</em>;
  };

  return (
    <div>
      <h3>Administrer Admins (Nivå 2)</h3>
      <p>Her kan du administrere alle kunde-administratorer (f.eks. styreledere).</p>
      
      {!showCreateForm && !editingAdmin && (
        <button onClick={() => setShowCreateForm(true)} style={{ margin: '20px 0' }}>
          + Opprett Ny Admin
        </button>
      )}
      {showCreateForm && (
        <CreateAdminForm 
          onAdminCreated={handleFormFinished}
          onCancel={() => setShowCreateForm(false)}
          owners={owners}
        />
      )}
      {editingAdmin && (
          <EditAdminForm
            initialData={editingAdmin}
            onFinished={handleFormFinished}
            owners={owners}
          />
      )}

      <h4>Eksisterende Admins</h4>
      {loading && <p>Laster admins...</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {!loading && !error && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Navn</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>E-post</th>
              {!isMobile && <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Tilknyttet Eier</th>}
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', width: '1%', whiteSpace: 'nowrap' }}>Handlinger</th>
            </tr>
          </thead>
          <tbody>
            {admins.map(admin => (
              <tr key={admin.uid}>
                <td style={{ border: '1px solid #ddd', padding: '8px' }}>{admin.displayName}</td>
                <td style={{ border: '1px solid #ddd', padding: '8px' }}>{admin.email}</td>
                {!isMobile && <td style={{ border: '1px solid #ddd', padding: '8px' }}>{getOwnerName(admin.customClaims?.ownerId)}</td>}
                <td style={{ border: '1px solid #ddd', padding: '8px', width: '1%', whiteSpace: 'nowrap' }}>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={() => setEditingAdmin(admin)}>Rediger</button>
                    <button onClick={() => onViewUsers(admin)}>Administrer Brukere</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default AdminManagement;
